import React, { useState, useContext, useEffect } from 'react';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Form from '@amzn/awsui-components-console/form';
import FormField from '@amzn/awsui-components-console/form-field';
import Header from '@amzn/awsui-components-console/header';
import Input from '@amzn/awsui-components-console/input';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { RegionContext } from '../../../CustomContext';
import { useNotifications } from '../../../NotificationContext';
import { useDrawer } from '../../../DrawerContext';
import { createGroup } from '../apis';
import { isAdmin } from '../../../lib/utils';

const GROUP_NAME_PREFIX = 'ds2-df-';

export default function CreateGroupPanel() {
    const [groupNameSuffix, setGroupNameSuffix] = useState('');
    const [ldap, setLdap] = useState('');
    const [loading, setLoading] = useState(false);
    const [privilegeLevel, setPrivilegeLevel] = useState(false);

    const { selectedRegion } = useContext(RegionContext);
    const { addError } = useNotifications();
    const { closeDrawer, getCallbacks } = useDrawer();

    useEffect(() => {
        isAdmin().then((response) => {
            setPrivilegeLevel(response.status);
        }).catch(() => {
            setPrivilegeLevel(false);
        });
    }, []);

    const fullGroupName = GROUP_NAME_PREFIX + groupNameSuffix;
    const isNameTooLong = fullGroupName.length > 255;

    const resetForm = () => {
        setGroupNameSuffix('');
        setLdap('');
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const groupName = GROUP_NAME_PREFIX + groupNameSuffix;
            await createGroup(groupName, ldap, selectedRegion.value);
            resetForm();
            const { onSuccess } = getCallbacks();
            if (onSuccess) {
                onSuccess(groupName);
            }
            closeDrawer();
        } catch (err) {
            const groupName = GROUP_NAME_PREFIX + groupNameSuffix;
            if (err.response?.data?.errorMessage) {
                addError(err.response.data.errorMessage, `Failed to create group ${groupName}`);
            } else {
                addError(
                    `An error occurred while creating group ${groupName}. Try again or contact support if the problem persists.`,
                    `Failed to create group ${groupName}`
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        resetForm();
        closeDrawer();
    };

    return (
        <Box padding="l">
            <Form
                header={<Header variant="h2">Create group</Header>}
                actions={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={!groupNameSuffix.trim() || !ldap.trim() || isNameTooLong || loading}
                            loading={loading}
                        >
                            Create
                        </Button>
                    </SpaceBetween>
                }
            >
                <SpaceBetween size="l">
                    <FormField
                        label="Group name"
                        description="Users in the LDAP group will have permission to the devices in this private group. Use the naming convention 'ds2-df-'."
                        constraintText="Enter a group name starting with 'ds2-df-' prefix (e.g., ds2-df-my-team). Maximum 255 characters."
                        errorText={isNameTooLong ? 'Group name must not exceed 255 characters.' : null}
                    >
                        <SpaceBetween direction="horizontal" size="xs">
                            <Box display="inline-block" padding={{ top: 'xxs' }}>
                                <span style={{ lineHeight: '32px' }}>{GROUP_NAME_PREFIX}</span>
                            </Box>
                            <Input
                                value={groupNameSuffix}
                                onChange={({ detail }) => setGroupNameSuffix(detail.value)}
                                placeholder="my-team"
                                ariaLabel="Group name suffix"
                            />
                        </SpaceBetween>
                    </FormField>
                    <FormField
                        label="LDAP"
                        description="Specify the LDAP group associated with this group."
                    >
                        <Input
                            value={ldap}
                            onChange={({ detail }) => setLdap(detail.value)}
                            placeholder="ldap-group-name"
                        />
                    </FormField>
                    <FormField
                        label="Privilege level"
                        description="The privilege level is set based on your current user privilege."
                    >
                        <Input value={privilegeLevel ? 'Admin' : 'User'} disabled />
                    </FormField>
                    <FormField
                        label="Third party (optional)"
                        description="Specify whether this is a third party group. This field is optional."
                    >
                        <Input value="No" disabled />
                    </FormField>
                </SpaceBetween>
            </Form>
        </Box>
    );
}
